import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useFormikContext } from "formik";
import * as yup from "yup";
import { EvidencePhotoUpload } from "../../EvidencePhotoUpload/EvidencePhotoUpload";
import { EvidencePhotoValue } from "../../EvidencePhotoUpload/useEvidencePhotoUpload";

export interface EvidenceCaptureValues {
  licensePlatePhoto: EvidencePhotoValue | null;
  violationPhoto: EvidencePhotoValue | null;
  violationDateTime: Date | null;
}

export interface EvidenceCaptureComponent extends React.FC {
  defaultInitialValues: EvidenceCaptureValues;
  validationSchema: yup.AnyObjectSchema;
}

export const EvidenceCapture: EvidenceCaptureComponent = () => {
  const { values, touched, errors, setFieldValue } = useFormikContext<EvidenceCaptureValues>();

  // Neither photo widget is a native input the officer can blur, so nothing marks these two
  // fields touched on its own the way a text field's onBlur would -- Formik's own handleSubmit
  // marks every field touched on a failed submit attempt regardless, which is what actually
  // surfaces these once the officer tries to submit without a photo.
  const licensePlatePhotoError =
    touched.licensePlatePhoto && typeof errors.licensePlatePhoto === "string" ? errors.licensePlatePhoto : undefined;
  const violationPhotoError =
    touched.violationPhoto && typeof errors.violationPhoto === "string" ? errors.violationPhoto : undefined;

  // violationDateTime tracks the license plate photo specifically -- it's the evidence that
  // establishes when the violation was observed, so removing or recapturing it should update
  // the timestamp too, not just the initial capture.
  const handleChange = (field: "licensePlatePhoto" | "violationPhoto") => (value: EvidencePhotoValue | null) => {
    setFieldValue(field, value);
    if (field === "licensePlatePhoto") {
      setFieldValue("violationDateTime", value ? new Date() : null);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Evidence Capture
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <EvidencePhotoUpload
            label="License Plate Capture"
            value={values.licensePlatePhoto}
            onChange={handleChange("licensePlatePhoto")}
            required
            requiredError={licensePlatePhotoError}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <EvidencePhotoUpload
            label="Vehicle in Violation"
            value={values.violationPhoto}
            onChange={handleChange("violationPhoto")}
            required
            requiredError={violationPhotoError}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

EvidenceCapture.defaultInitialValues = {
  licensePlatePhoto: null,
  violationPhoto: null,
  violationDateTime: null,
};

// .nullable() so a null value passes the base type check and .required() is what actually
// produces our custom message -- without it, yup's own generic type-mismatch error would win
// instead (mixed() isn't nullable by default).
EvidenceCapture.validationSchema = yup.object({
  licensePlatePhoto: yup.mixed<EvidencePhotoValue>().nullable().required("A License Plate Capture photo is required"),
  violationPhoto: yup.mixed<EvidencePhotoValue>().nullable().required("A Vehicle in Violation photo is required"),
  // PrefilledDetails lets the officer clear either half of this field (see its handleDateChange/
  // handleTimeChange) to correct an auto-populated value -- without this, submitting while it's
  // still null falls back to GenerateCitation's onSubmit defaulting to the current time instead
  // of the actual observed violation time, with no validation error to warn the officer.
  // .typeError() is a backstop, not the primary defense -- PrefilledDetails' own handlers already
  // convert an Invalid Date to null before it gets this far, but without this too, any Invalid
  // Date that did reach here would surface yup's raw type-error string instead of our own copy.
  violationDateTime: yup
    .date()
    .nullable()
    .typeError("Violation date and time are required")
    .required("Violation date and time are required"),
});

export default EvidenceCapture;
